import { Controller } from "@hotwired/stimulus"
import { DASHBOARD_CHART_BASE_OPTIONS, fillFor } from "../dashboards/apex_chart_defaults"

// Generic ApexCharts wrapper reused by every Percurso chart (donut, bar,
// line, pie) instead of one bespoke controller per chart type. Dashboard
// chart widgets opt into the shared purple-theme base styling (transparent
// bg, no toolbar, straight stroke, no markers, gradient fill, hidden
// legend — see design.md task 5.4) via `dashboardValue`; Percurso's charts
// leave it unset and keep their current look untouched.
export default class extends Controller {
  static values = {
    type:      String,
    series:    Array,
    options:   Object,
    dashboard: Boolean
  }

  connect() {
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1))
    const cancel = window.cancelIdleCallback || clearTimeout
    this.renderHandle = schedule(() => this.renderChart())
    this.cancelSchedule = cancel
  }

  disconnect() {
    if (this.renderHandle && this.cancelSchedule) {
      this.cancelSchedule(this.renderHandle)
      this.renderHandle = null
    }
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    if (!window.ApexCharts) return

    const isDark    = document.documentElement.classList.contains("dark")
    // Dashboard charts use the purple theme's own text-secondary tokens
    // (config/tailwind.config.js `dashboard`/`dashboard-dark`) so axis/label
    // text matches the surrounding HTML in both modes; other charts (e.g.
    // Percurso) keep the pre-existing neutral gray.
    const foreColor = this.dashboardValue
      ? (isDark ? "#9CA3AF" : "#6B7280")
      : (isDark ? "#d1d5db" : "#373d3f")
    const base = this.dashboardValue ? DASHBOARD_CHART_BASE_OPTIONS : {}
    const fill = this.dashboardValue ? fillFor(this.typeValue) : {}

    // optionsValue may bring its own `chart`/`theme`/`tooltip`/etc sub-objects
    // (toolbar, zoom, ...) — merge those in rather than spreading over them,
    // otherwise a plain spread would wipe out the dark-mode foreColor/theme
    // or the dashboard base styling.
    const config = {
      ...base,
      ...this.optionsValue,
      chart:   { ...base.chart, type: this.typeValue, height: this.optionsValue.height || (this.dashboardValue ? "100%" : 300), foreColor, ...this.optionsValue.chart },
      colors:  base.colors,
      stroke:  { ...base.stroke, ...this.optionsValue.stroke },
      markers: { ...base.markers, ...this.optionsValue.markers },
      fill:    { ...fill, ...this.optionsValue.fill },
      legend:  { ...base.legend, ...this.optionsValue.legend },
      theme:   { mode: isDark ? "dark" : "light", ...this.optionsValue.theme },
      tooltip: { theme: isDark ? "dark" : "light", ...this.optionsValue.tooltip },
      series:  this.seriesValue
    }

    this.chart = new ApexCharts(this.element, config)
    this.chart.render()
  }
}
